import React from 'react';
import styled from '../../../../../style/styled';
import { useSelector } from 'react-redux';
import { ScrollPosition } from 'react-lazy-load-image-component';
import { SCREEN_WIDTH_BREAK_SMALL } from '../../../../../style/SIZES';
import {
  selectLanguage,
  selectPlaylistsMapInCart,
  selectPromoCodeDiscountRate,
} from '../../../../../store/app/selectors';
import { Grid } from '../../../../../Layouts/primitives/Grid';
import {
  ContentSectionWidgetContainer,
  ContentSectionWidgetImageAspectRatioBox,
  ContentSectionWidgetImageAspectRatioBoxContent,
  ContentSectionWidgetImageAspectRatioBoxContentWrapper,
  ContentSectionWidgetTitle,
} from '../../../styledComponents';
import { TourWidgetOfflineStatusIndicator } from '../TourWidgetOfflineStatusIndicator';
import { isMobile } from 'react-device-detect';
import { TourWidgetDefaultCheckInButton } from '../TourWidgetDefaultCheckInButton';
import { TourWidgetGoToGateButton } from '../TourWidgetGoToGateButton';
import { TourWidgetBookNowButton } from '../TourWidgetBookNowButton';
import { getExpiryInfo } from '../../getExpiryInfo';
import { TourWidgetPrice } from '../TourWidgetPrice';
import { TourWidgetTestTourIndicator } from '../TourWidgetTestTourIndicator';
import { COLORS } from '../../../../../style/colors';
import { TourWidgetPastThumbnail } from '../TourWidgetPastThumbnail';
import { TourWidgetPresentThumbnail } from '../TourWidgetPresentThumbnail';
import { TourWidgetContinueTourButton } from '../TourWidgetContinueTourButton';
// import { unlock } from '../../../features/audio/howlerSetup';
import { useTranslation } from 'react-i18next';
import { getI18nFieldValue } from '../../../../../features/i18n/getI18nFieldValue';
import { TourWidgetItineraryButtonConditional } from '../TourWidgetItineraryButtonConditional';
import { Asset, I18nInfoField } from '../../../../../types';
import { CurrencyCode } from '../../../../../graphql/globalTypes';
import { TourWidgetView } from './TourWidgetView';

export type TourWidgetTour = {
  id: string;
  internalReference: string;
  published?: boolean;
  trial?: boolean;
  nameI18n: I18nInfoField;
  pastThumbnail: Asset | null;
  presentThumbnail: Asset | null;
  currencyCode: CurrencyCode;
  unitPrice: number;
  priceInCents: boolean;
  purchased: boolean;
  pinned?: boolean;
};

export interface Props {
  buyTour: (tourID: string) => void;
  expiresAt?: string | null;
  isGift?: boolean;
  tour: TourWidgetTour;
  LeftButton?: React.ReactChild;
  RightButton?: React.ReactChild;
  scrollPosition?: ScrollPosition;
  opensItineraryPage?: boolean;
  withPriceHidden?: boolean;
}

export const TourWidget: React.FC<Props> = ({
  buyTour,
  expiresAt,
  tour,
  LeftButton,
  RightButton,
  isGift,
  scrollPosition,
  opensItineraryPage = false,
  withPriceHidden = false,
}) => {
  const playlistsMapInCart = useSelector(selectPlaylistsMapInCart);
  const language = useSelector(selectLanguage);
  const discountRate = useSelector(selectPromoCodeDiscountRate);

  const { expiresIn, expiresSoon } = getExpiryInfo(expiresAt, language);

  const isInCart = Boolean(playlistsMapInCart[tour.id]);

  const nonInteractiveImageURI = tour.presentThumbnail?.uri;
  const interactiveImageURI = tour.pastThumbnail?.uri;

  const priceDisplayed =
    !withPriceHidden && !tour.trial && (!tour.purchased || isGift);
  const downloadable = (tour.purchased || tour.trial) && !isGift;
  const tourName = getI18nFieldValue(tour.nameI18n, language);

  return (
    <TourWidgetView
      buyTour={buyTour}
      currencyCode={tour.currencyCode}
      discountRate={discountRate}
      downloadable={downloadable}
      expiresIn={expiresIn}
      expiresSoon={expiresSoon}
      id={tour.id}
      interactiveImageURI={interactiveImageURI}
      internalReference={tour.internalReference}
      isInCart={isInCart}
      LeftButton={LeftButton}
      nonInteractiveImageURI={nonInteractiveImageURI}
      opensItineraryPage={opensItineraryPage}
      priceDisplayed={priceDisplayed}
      priceInCents={tour.priceInCents}
      published={tour.published}
      purchased={tour.purchased}
      RightButton={RightButton}
      scrollPosition={scrollPosition}
      tourName={tourName}
      unitPrice={tour.unitPrice}
      pinned={tour.pinned}
    />
  );
};
